/*
  About page behaviours, shared by the c-about-* sections.
  Each section loads this file, so it guards against running twice and
  initialises per section root, including after a theme editor reload.
*/
(function () {
  if (window.cAbout) return;

  var motionOK = window.matchMedia('(prefers-reduced-motion: no-preference)');

  /* ---------- Story: paragraph and word reveal ---------- */

  function splitWords(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(function (node) {
      if (!node.nodeValue.trim()) return;
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else {
          var span = document.createElement('span');
          span.className = 'c-about-story__word';
          span.textContent = part;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });

    return el.querySelectorAll('.c-about-story__word');
  }

  // All paragraphs reveal together, once, as soon as the text starts to come
  // into view. Words fade in as one quick sweep so the full copy is readable early.
  function initStory(root) {
    var paragraphs = root.querySelectorAll('[data-c-about-story-reveal]');
    if (!paragraphs.length || !motionOK.matches || !('IntersectionObserver' in window)) return;
    if (root.hasAttribute('data-c-about-armed')) return;

    var words = [];
    paragraphs.forEach(function (p) {
      Array.prototype.push.apply(words, splitWords(p));
    });
    var step = Math.min(18, 1400 / Math.max(1, words.length));
    words.forEach(function (word, i) {
      word.style.transitionDelay = Math.round(i * step) + 'ms';
    });
    root.setAttribute('data-c-about-armed', '');

    var io = new IntersectionObserver(
      function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        root.classList.add('is-revealed');
        io.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );
    io.observe(paragraphs[0]);
  }

  /* ---------- Swiper loading ---------- */

  function whenSwiper(fn) {
    if (typeof window.Swiper !== 'undefined') return fn();
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (typeof window.Swiper !== 'undefined') {
        clearInterval(timer);
        fn();
      } else if (tries > 100) {
        clearInterval(timer);
      }
    }, 100);
  }

  function cssPx(el, name) {
    return parseFloat(getComputedStyle(el).getPropertyValue(name)) || 0;
  }

  /* ---------- Timeline ---------- */

  var desktopMQ = window.matchMedia('(min-width: 750px)');

  function initTimeline(root) {
    if (!root.hasAttribute('data-c-about-timeline')) return;
    var el = root.querySelector('[data-c-tl-swiper]');
    if (!el) return;

    whenSwiper(function () {
      if (root.cAboutTimeline) root.cAboutTimeline.destroy();

      var viewport = root.querySelector('[data-c-tl-viewport]');
      var rail = root.querySelector('[data-c-tl-rail]');
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-c-tl-dot]'));
      var counter = root.querySelector('[data-c-tl-counter]');
      var prev = root.querySelector('[data-c-slider-prev]');
      var next = root.querySelector('[data-c-slider-next]');
      var slides = el.querySelectorAll('.swiper-slide');
      var total = slides.length;
      var WINDOW = 5;
      var swiper = null;
      var isDesktop = desktopMQ.matches;

      // Desktop: the current card widens, so the track position is worked out
      // here and Swiper only drives input, state and accessibility.
      function placeTrack(index) {
        if (!isDesktop) return;
        var card = cssPx(root, '--c-tl-card');
        var active = cssPx(root, '--c-tl-card-active');
        var gap = cssPx(root, '--c-tl-gap');
        var vw = el.clientWidth;
        var container = root.querySelector('.c-container');
        var inset = container ? container.getBoundingClientRect().left - root.getBoundingClientRect().left + cssPx(container, 'padding-left') : 0;
        var trackW = (total - 1) * (card + gap) + active;
        var x = vw / 2 - (index * (card + gap) + active / 2);
        var maxX = inset;
        var minX = vw - trackW - inset;
        if (minX > maxX) minX = maxX;
        x = Math.min(maxX, Math.max(minX, x));
        // Swiper writes its own transition-duration here; match the card widening.
        swiper.wrapperEl.style.transitionDuration = motionOK.matches ? '500ms' : '0ms';
        swiper.wrapperEl.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      }

      function updateRail(index) {
        if (!rail) return;
        var start = Math.max(0, Math.min(index - Math.floor(WINDOW / 2), total - WINDOW));
        if (total <= WINDOW) start = 0;
        var span = Math.min(WINDOW, total) - 1 || 1;

        dots.forEach(function (dot, i) {
          var inWindow = i >= start && i < start + WINDOW;
          var k = i - start;
          dot.style.setProperty('--c-tl-pos', (k / span) * 100 + '%');
          dot.style.setProperty('--c-tl-pos-all', (total > 1 ? (i / (total - 1)) * 100 : 0) + '%');
          dot.hidden = !inWindow;
          dot.classList.toggle('is-last', inWindow && k === span);
          dot.classList.toggle('is-active', i === index);
          dot.classList.toggle('is-past', i < index);
          if (i === index) dot.setAttribute('aria-current', 'step');
          else dot.removeAttribute('aria-current');
        });

        rail.style.setProperty('--c-tl-fill', ((index - start) / span) * 100 + '%');
        rail.style.setProperty('--c-tl-fill-all', (total > 1 ? (index / (total - 1)) * 100 : 0) + '%');
      }

      function sync() {
        var index = swiper.activeIndex;
        for (var i = 0; i < slides.length; i++) {
          slides[i].classList.toggle('is-current', i === index);
        }
        if (counter) counter.textContent = index + 1 + ' ' + (counter.getAttribute('data-c-tl-of') || 'OF') + ' ' + total;
        updateRail(index);
        placeTrack(index);
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === total - 1;
      }

      function mobileOffsetAfter() {
        return Math.max(16, el.clientWidth - cssPx(root, '--c-tl-card') - 16);
      }

      function build() {
        isDesktop = desktopMQ.matches;
        if (swiper) swiper.destroy(true, true);
        el.querySelector('.swiper-wrapper').style.transform = '';

        var start = isDesktop ? parseInt(root.getAttribute('data-initial'), 10) || 0 : 0;
        start = Math.max(0, Math.min(total - 1, start));

        var options = {
          slidesPerView: 'auto',
          spaceBetween: cssPx(root, '--c-tl-gap'),
          initialSlide: start,
          speed: motionOK.matches ? 500 : 0,
          watchOverflow: false,
          a11y: { enabled: false },
          on: {
            // These can fire from inside the constructor, before it returns.
            init: function () {
              swiper = this;
              sync();
            },
            slideChange: function () {
              swiper = this;
              sync();
            },
            resize: function () {
              swiper = this;
              if (!isDesktop) {
                swiper.params.slidesOffsetAfter = mobileOffsetAfter();
                swiper.update();
              }
              sync();
            },
          },
        };

        if (isDesktop) {
          options.centeredSlides = true;
          options.virtualTranslate = true;
        } else {
          options.slidesOffsetBefore = 16;
          options.slidesOffsetAfter = mobileOffsetAfter();
        }

        swiper = new window.Swiper(el, options);
      }

      function onPrev() { swiper.slidePrev(); }
      function onNext() { swiper.slideNext(); }
      function onDot(e) {
        var i = parseInt(e.currentTarget.getAttribute('data-c-tl-dot'), 10);
        if (!isNaN(i)) swiper.slideTo(i);
      }
      // Clicking a card that is not the current one moves the timeline to it.
      // A card that is a link only follows the link once it is current. Swiper
      // already swallows the click that ends a drag, so a swipe never lands here.
      function onSlideClick(e) {
        if (!swiper || e.defaultPrevented) return;
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var i = Array.prototype.indexOf.call(slides, e.currentTarget);
        if (i < 0 || i === swiper.activeIndex) return;
        e.preventDefault();
        swiper.slideTo(i);
      }
      function onKey(e) {
        if (e.key === 'ArrowRight') { swiper.slideNext(); e.preventDefault(); }
        else if (e.key === 'ArrowLeft') { swiper.slidePrev(); e.preventDefault(); }
      }
      function onBreakpoint() { build(); }

      if (prev) prev.addEventListener('click', onPrev);
      if (next) next.addEventListener('click', onNext);
      dots.forEach(function (d) { d.addEventListener('click', onDot); });
      Array.prototype.forEach.call(slides, function (s) { s.addEventListener('click', onSlideClick); });
      if (viewport) viewport.addEventListener('keydown', onKey);
      desktopMQ.addEventListener('change', onBreakpoint);

      build();

      root.cAboutTimeline = {
        destroy: function () {
          if (prev) prev.removeEventListener('click', onPrev);
          if (next) next.removeEventListener('click', onNext);
          dots.forEach(function (d) { d.removeEventListener('click', onDot); });
          Array.prototype.forEach.call(slides, function (s) { s.removeEventListener('click', onSlideClick); });
          if (viewport) viewport.removeEventListener('keydown', onKey);
          desktopMQ.removeEventListener('change', onBreakpoint);
          if (swiper) swiper.destroy(true, true);
        },
        swiper: function () { return swiper; },
      };
    });
  }

  /* ---------- Mobile carousels ---------- */

  // Card rows that are static above 750px and a Swiper carousel with dots below.
  function initCarousels(root) {
    var els = root.querySelectorAll('[data-c-about-carousel]');
    if (!els.length) return;

    whenSwiper(function () {
      els.forEach(function (el) {
        if (el.cAboutCarousel) el.cAboutCarousel.destroy();

        var dots = el.parentNode.querySelector('[data-c-about-carousel-dots]');
        var swiper = null;

        var slides = el.querySelectorAll('.swiper-slide');

        // Above 750px the cards are a static row, so they stop announcing as a carousel.
        function setCarouselSemantics(on) {
          [el].concat(Array.prototype.slice.call(slides)).forEach(function (node) {
            var desc = node === el ? 'carousel' : 'slide';
            var role = node === el ? 'region' : 'group';
            if (on) {
              node.setAttribute('role', role);
              node.setAttribute('aria-roledescription', desc);
            } else {
              node.removeAttribute('aria-roledescription');
              if (node !== el) node.removeAttribute('role');
            }
          });
        }

        function build() {
          setCarouselSemantics(!desktopMQ.matches);
          if (desktopMQ.matches) {
            if (swiper) {
              swiper.destroy(true, true);
              swiper = null;
            }
            return;
          }
          if (swiper) return;
          swiper = new window.Swiper(el, {
            slidesPerView: 'auto',
            spaceBetween: parseInt(el.getAttribute('data-gap'), 10) || 12,
            speed: motionOK.matches ? 400 : 0,
            watchOverflow: true,
            pagination: dots
              ? {
                  el: dots,
                  clickable: true,
                  bulletClass: 'c-about-dot',
                  bulletActiveClass: 'is-active',
                  bulletElement: 'button',
                }
              : false,
            a11y: { enabled: true, paginationBulletMessage: 'Go to card {{index}}' },
          });
        }

        desktopMQ.addEventListener('change', build);
        build();

        el.cAboutCarousel = {
          destroy: function () {
            desktopMQ.removeEventListener('change', build);
            if (swiper) swiper.destroy(true, true);
          },
        };
      });
    });
  }

  /* ---------- In-page anchors ---------- */

  // From 990px Horizon scrolls .page-wrapper rather than the window.
  function scroller() {
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight && getComputedStyle(wrapper).overflowY !== 'visible') {
      return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  function headerOffset() {
    var fromVar = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    if (fromVar) return fromVar;
    var header = document.getElementById('header-component');
    return header ? header.offsetHeight : 0;
  }

  // Scrolls to an in-page target so it lands below the sticky header, whose
  // height is read at the moment of the click.
  function initAnchors(root) {
    root.querySelectorAll('a[data-c-about-anchor-link]').forEach(function (link) {
      if (link.hasAttribute('data-c-about-anchor-bound')) return;
      link.setAttribute('data-c-about-anchor-bound', '');
      link.addEventListener('click', function (e) {
        var hash = link.getAttribute('href') || '';
        if (hash.charAt(0) !== '#' || hash.length < 2) return;
        var target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (!target) return;
        e.preventDefault();
        var box = scroller();
        var boxTop = box === document.scrollingElement || box === document.documentElement ? 0 : box.getBoundingClientRect().top;
        var top = target.getBoundingClientRect().top - boxTop + box.scrollTop - headerOffset();
        box.scrollTo({ top: Math.max(0, top), behavior: motionOK.matches ? 'smooth' : 'auto' });
        if (history.pushState) history.pushState(null, '', hash);
        var focusable = target.querySelector('h2, h1') || target;
        if (!focusable.hasAttribute('tabindex')) focusable.setAttribute('tabindex', '-1');
        focusable.focus({ preventScroll: true });
      });
    });
  }

  /* ---------- Story pop-up ---------- */

  // Links in the About sections that point at a blog article (the founders'
  // stories, a timeline card) open the article in a scrollable pop-up instead
  // of leaving the page. Every article link in the same section becomes a tab,
  // so "Read their story" shows both founders. Any failure falls back to the
  // normal link, as does a modifier-click or a middle-click.

  var ARTICLE_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?blogs\/[^/]+\/[^/]+\/?$/i;
  var storyCache = {};
  var modal = null;

  function articleKey(link) {
    if (!link || !link.href) return null;
    var url;
    try { url = new URL(link.href, window.location.href); } catch (err) { return null; }
    if (url.origin !== window.location.origin) return null;
    if (!ARTICLE_PATH.test(url.pathname)) return null;
    return url.pathname.replace(/\/$/, '');
  }

  function storySection(link) {
    return link.closest('.shopify-section[class*="section-c-about-"]');
  }

  function collectStories(section, clicked) {
    var stories = [];
    var byKey = {};
    section.querySelectorAll('a[href]').forEach(function (a) {
      var key = articleKey(a);
      if (!key) return;
      var label = (a.getAttribute('data-c-about-story-label') || '').trim();
      if (!byKey[key]) {
        byKey[key] = { key: key, href: a.href, label: label };
        stories.push(byKey[key]);
      } else if (!byKey[key].label && label) {
        byKey[key].label = label;
      }
    });
    var active = 0;
    var clickedKey = articleKey(clicked);
    stories.forEach(function (s, i) { if (s.key === clickedKey) active = i; });
    return { stories: stories, active: active };
  }

  function cleanNode(root) {
    root.querySelectorAll('script, style, link, noscript').forEach(function (n) { n.remove(); });
    root.querySelectorAll('*').forEach(function (el) {
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
      });
      // Ids from the article page could clash with this one.
      if (el.id) el.removeAttribute('id');
    });
    return root;
  }

  function fetchStory(story) {
    if (storyCache[story.key]) return storyCache[story.key];
    storyCache[story.key] = fetch(story.href, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-c-article-content]') || doc.querySelector('main article') || doc.querySelector('main');
        if (!content) throw new Error('No article content');
        var titleEl = doc.querySelector('.c-article-hero__title') || doc.querySelector('main h1');
        var media = doc.querySelector('.c-article-hero__media img');
        return {
          title: titleEl ? titleEl.textContent.trim() : '',
          media: media ? cleanNode(media.cloneNode(true)) : null,
          content: cleanNode(content.cloneNode(true)),
        };
      });
    storyCache[story.key].catch(function () { delete storyCache[story.key]; });
    return storyCache[story.key];
  }

  function buildModal() {
    var dialog = document.createElement('dialog');
    dialog.className = 'c-about-modal';
    dialog.setAttribute('aria-labelledby', 'c-about-modal-title');
    dialog.innerHTML =
      '<div class="c-about-modal__panel">' +
        '<div class="c-about-modal__bar">' +
          '<div class="c-about-modal__tabs" role="tablist" aria-label="Stories" data-c-modal-tabs></div>' +
          '<button type="button" class="c-about-modal__close" aria-label="Close" data-c-modal-close>' +
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="c-about-modal__body" role="tabpanel" tabindex="-1" data-c-modal-body></div>' +
      '</div>';
    document.body.appendChild(dialog);

    var state = { dialog: dialog, stories: [], active: 0, returnFocus: null, token: 0 };
    var tabs = dialog.querySelector('[data-c-modal-tabs]');
    var body = dialog.querySelector('[data-c-modal-body]');

    function renderTabs() {
      tabs.innerHTML = '';
      tabs.hidden = state.stories.length < 2;
      state.stories.forEach(function (story, i) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'c-about-modal__tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', i === state.active ? 'true' : 'false');
        tab.tabIndex = i === state.active ? 0 : -1;
        tab.textContent = story.label || story.title || 'Story ' + (i + 1);
        tab.addEventListener('click', function () { show(i); });
        tabs.appendChild(tab);
      });
    }

    function show(i, opts) {
      state.active = i;
      renderTabs();
      var story = state.stories[i];
      var token = ++state.token;
      body.setAttribute('aria-busy', 'true');
      body.innerHTML = '<p class="c-about-modal__status">Loading…</p>';
      body.scrollTop = 0;

      fetchStory(story).then(function (data) {
        if (token !== state.token) return;
        story.title = data.title;
        if (!story.label) renderTabs();
        body.innerHTML = '';
        var article = document.createElement('article');
        article.className = 'c-about-modal__article';
        var h = document.createElement('h2');
        h.className = 'c-about-modal__title';
        h.id = 'c-about-modal-title';
        h.textContent = data.title || story.label || '';
        article.appendChild(h);
        if (data.media) {
          var fig = document.createElement('div');
          fig.className = 'c-about-modal__media';
          fig.appendChild(data.media.cloneNode(true));
          article.appendChild(fig);
        }
        var content = data.content.cloneNode(true);
        content.className = 'c-about-modal__content';
        article.appendChild(content);
        var more = document.createElement('a');
        more.className = 'c-about-modal__more';
        more.href = story.href;
        more.textContent = 'Open the full article';
        article.appendChild(more);
        body.appendChild(article);
        body.removeAttribute('aria-busy');
      }).catch(function () {
        if (token !== state.token) return;
        // The story the visitor clicked could not load: follow the link instead.
        if (opts && opts.navigateOnFail) {
          window.location.href = story.href;
          return;
        }
        body.removeAttribute('aria-busy');
        body.innerHTML = '';
        var p = document.createElement('p');
        p.className = 'c-about-modal__status';
        var a = document.createElement('a');
        a.href = story.href;
        a.textContent = 'Read this story on its own page';
        p.appendChild(document.createTextNode('This story could not be loaded here. '));
        p.appendChild(a);
        body.appendChild(p);
      });
    }

    function lock(on) {
      if (on) document.documentElement.setAttribute('scroll-lock', '');
      else document.documentElement.removeAttribute('scroll-lock');
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    dialog.addEventListener('close', function () {
      state.token++;
      lock(false);
      var target = state.returnFocus;
      state.returnFocus = null;
      if (target && document.contains(target)) target.focus({ preventScroll: true });
    });

    // A click on the backdrop lands on the dialog itself, outside the panel.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close();
    });
    dialog.querySelector('[data-c-modal-close]').addEventListener('click', close);

    tabs.addEventListener('keydown', function (e) {
      var n = state.stories.length;
      if (n < 2) return;
      var i = state.active;
      if (e.key === 'ArrowRight') i = (i + 1) % n;
      else if (e.key === 'ArrowLeft') i = (i - 1 + n) % n;
      else if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = n - 1;
      else return;
      e.preventDefault();
      show(i);
      var tab = tabs.children[i];
      if (tab) tab.focus();
    });

    state.open = function (stories, active, returnFocus) {
      state.stories = stories;
      state.returnFocus = returnFocus;
      if (!dialog.open) {
        dialog.showModal();
        lock(true);
      }
      show(active, { navigateOnFail: true });
      var focusTarget = stories.length > 1 ? tabs.children[active] : dialog.querySelector('[data-c-modal-close]');
      if (focusTarget) focusTarget.focus();
    };

    return state;
  }

  function onStoryClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = e.target.closest && e.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    if (link.closest('.c-about-modal')) return;
    if (!articleKey(link)) return;
    var section = storySection(link);
    if (!section) return;
    if (typeof HTMLDialogElement === 'undefined' || !('showModal' in HTMLDialogElement.prototype)) return;
    if (!window.fetch || !window.DOMParser) return;

    var found = collectStories(section, link);
    if (!found.stories.length) return;
    e.preventDefault();
    if (!modal) modal = buildModal();
    modal.open(found.stories, found.active, link);
  }

  document.addEventListener('click', onStoryClick);

  /* ---------- Boot ---------- */

  var initialisers = [initStory, initTimeline, initCarousels, initAnchors];

  function initRoot(root) {
    initialisers.forEach(function (fn) {
      fn(root);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-c-about]').forEach(initRoot);
  }

  window.cAbout = { init: initAll };

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
